'use client';

import Image from 'next/image';
import { Check, X } from 'phosphor-icons';
import * as React from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Button } from '../../atoms/Button/Button';
import { EntitlementConfirmationProps } from './EntitlementConfirmation.types';
import { useEntitlementConfirmation } from './useEntitlementConfirmation';

export const EntitlementConfirmation = ({
  type,
  productName,
  productImage,
  microcopy,
  onGoToMyAssets,
  onOpenInUnityFromEntitlement,
  onSeeOwnedPackages,
  onCloseFromEntitlement,
  className = '',
}: EntitlementConfirmationProps) => {
  const { isOpen, open, close } = useEntitlementConfirmation();

  React.useLayoutEffect(() => {
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    close();
    onCloseFromEntitlement();
  };

  if (!isOpen) return null;

  return (
    <section
      data-test="entitlement-confirmation-section"
      role="status"
      aria-live="polite"
      className={c('relative w-full bg-grey-800 py-8 text-white', className)}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={handleClose}
        className="absolute right-4 top-4"
      >
        <X width={24} height={24} />
      </button>

      <div className="lg-container flex flex-col gap-4 md:flex-row md:items-center md:gap-6">
        <div className="flex shrink-0 items-center gap-2">
          <span className="body-small">{microcopy.addedToMyAsset}</span>
          <span
            aria-hidden="true"
            className="bg-emerald-500 inline-flex h-6 w-6 items-center justify-center rounded-full"
          >
            <Check width={24} height={24} />
          </span>
        </div>

        <div className="flex min-w-0 items-center gap-3 md:gap-4">
          <Image
            src={productImage}
            alt={productName}
            className="h-16 w-24 rounded-lg object-cover"
            width={110}
            height={74}
          />
          <h3 className="body-small min-w-0 max-w-64">{productName}</h3>
        </div>

        <div className="mt-1 flex items-center gap-3 md:ml-auto [&_span]:uppercase [&_span]:tracking-[0.0875rem]">
          <Button
            type="button"
            onClick={onGoToMyAssets}
            label={microcopy.goToMyAsset}
            size="tiny"
            style="secondaryMonochrome"
            edge="rounded"
            isDarkMode
          />

          {type === 'multi-package' ? (
            <Button
              type="button"
              onClick={onSeeOwnedPackages}
              label={microcopy.seeOwnedPackages}
              size="tiny"
              style="whiteBg"
              edge="rounded"
              isDarkMode
            />
          ) : (
            <Button
              type="button"
              onClick={onOpenInUnityFromEntitlement}
              label={microcopy.openInUnity}
              size="tiny"
              style="whiteBg"
              edge="rounded"
              isDarkMode
            />
          )}
        </div>
      </div>
    </section>
  );
};
